import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  WifiOff, 
  RefreshCw, 
  Home, 
  Smartphone,
  ArrowLeft,
  Wifi
} from 'lucide-react'

const OfflinePage: React.FC = () => {
  const handleRetry = () => {
    window.location.reload()
  }

  const handleGoHome = () => {
    window.location.href = '/'
  }

  return (
    <>
      <Helmet>
        <title>离线模式 - DeepLink Limited</title>
        <meta name="description" content="您正在离线模式下使用DeepLink官网" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* 离线图标 */}
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <div className="w-32 h-32 mx-auto bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900/20 dark:to-pink-900/20 rounded-full flex items-center justify-center">
                <WifiOff className="w-16 h-16 text-red-600 dark:text-red-400" />
              </div>
            </motion.div>

            {/* 标题和描述 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-8"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                离线模式
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                您当前处于离线状态，但不用担心！我们的应用支持离线使用
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <Smartphone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100">
                    CoreTrust手机
                  </h2>
                </div>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  即使在离线状态下，您也可以查看CoreTrust手机的基本信息和功能特性
                </p>
              </div>
            </motion.div>

            {/* 功能列表 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mb-12"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                离线可用功能
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {[
                  { name: '产品介绍', status: '可用' },
                  { name: '技术规格', status: '可用' },
                  { name: '公司信息', status: '可用' },
                  { name: '联系方式', status: '可用' },
                  { name: '新闻动态', status: '部分可用' },
                  { name: '在线服务', status: '需要网络' }
                ].map((feature, index) => (
                  <div
                    key={feature.name}
                    className={`p-4 rounded-lg border ${
                      feature.status === '可用'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : feature.status === '部分可用'
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <h4 className={`font-semibold text-sm ${
                      feature.status === '可用'
                        ? 'text-green-900 dark:text-green-100'
                        : feature.status === '部分可用'
                        ? 'text-yellow-900 dark:text-yellow-100'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {feature.name}
                    </h4>
                    <p className={`text-xs ${
                      feature.status === '可用'
                        ? 'text-green-700 dark:text-green-300'
                        : feature.status === '部分可用'
                        ? 'text-yellow-700 dark:text-yellow-300'
                        : 'text-gray-500 dark:text-gray-500'
                    }`}>
                      {feature.status}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 操作按钮 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={handleRetry}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-5 h-5" />
                <span>重新连接</span>
              </button>
              <button
                onClick={handleGoHome}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300 flex items-center justify-center space-x-2"
              >
                <Home className="w-5 h-5" />
                <span>返回首页</span>
              </button>
            </motion.div>

            {/* 网络状态指示 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="mt-12"
            >
              <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-500">
                <WifiOff className="w-4 h-4" />
                <span className="text-sm">网络连接已断开</span>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-600 mt-2">
                当网络恢复时，应用将自动同步最新内容
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default OfflinePage